import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { drawingService } from '../../../services/drawingService';
import { useLanguage } from '../../../contexts/LanguageContext';

// ─── Upload Drawing Modal ────────────────────────────────────────────────────
const UploadDrawingModal = ({ siteId, siteData, onClose, onSuccess }) => {
  const { userProfile } = useAuth();
  const [form, setForm] = useState({
    drawingName: '',
    drawingNumber: '',
    revision: '',
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selected = e?.target?.files?.[0];
    if (selected && selected?.type !== 'application/pdf') {
      setError('Only PDF files are accepted for drawings.');
      return;
    }
    setError('');
    setFile(selected || null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!file) { setError('Please select a PDF file.'); return; }
    if (!form?.drawingName?.trim()) { setError('Drawing Name is required.'); return; }
    if (!form?.drawingNumber?.trim()) { setError('Drawing Number is required.'); return; }
    if (!form?.revision?.trim()) { setError('Revision is required.'); return; }

    try {
      setUploading(true);
      setError('');
      const result = await drawingService?.uploadDrawing(file, {
        siteId,
        companyId: siteData?.company_id,
        drawingName: form?.drawingName?.trim(),
        drawingNumber: form?.drawingNumber?.trim(),
        revision: form?.revision?.trim(),
        versionNumber: 1,
      });

      if (result?.error) {
        const err = result?.error;
        console.error('[DrawingsTab] upload failed:', { code: err?.code, message: err?.message, details: err?.details, hint: err?.hint });
        if (err?.code === 'PGRST301' || err?.code === '42501') {
          setError(`Upload failed — permission denied: ${err?.message ?? `code ${err?.code}`}`);
        } else if (err?.message) {
          setError(`Upload failed: ${err?.message}`);
        } else {
          // Real error with no message — surface code/details so the cause is at least diagnosable.
          setError(`Upload failed (code ${err?.code ?? 'unknown'}${err?.details ? `: ${err?.details}` : ''}). Please try again or contact support.`);
        }
      } else {
        onSuccess?.();
      }
    } catch (err) {
      console.error('[DrawingsTab] upload threw:', { code: err?.code, message: err?.message });
      setError(`Upload failed: ${err?.message ?? 'unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-elevation-3 w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Upload Drawing</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-smooth">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Drawing Name <span className="text-error">*</span>
            </label>
            <Input
              type="text"
              placeholder="e.g. Ground Floor Plan"
              value={form?.drawingName}
              onChange={(e) => setForm(f => ({ ...f, drawingName: e?.target?.value }))}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Drawing Number <span className="text-error">*</span>
              </label>
              <Input
                type="text"
                placeholder="e.g. A-101"
                value={form?.drawingNumber}
                onChange={(e) => setForm(f => ({ ...f, drawingNumber: e?.target?.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Revision <span className="text-error">*</span>
              </label>
              <Input
                type="text"
                placeholder="e.g. Rev A"
                value={form?.revision}
                onChange={(e) => setForm(f => ({ ...f, revision: e?.target?.value }))}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              PDF File <span className="text-error">*</span>
            </label>
            <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-smooth ${
              file ? 'border-success bg-success/5' : 'border-border hover:border-accent bg-muted/30 hover:bg-accent/5'
            }`}>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              {file ? (
                <div className="flex flex-col items-center gap-1 px-4 text-center">
                  <Icon name="FileCheck" size={28} className="text-success" />
                  <span className="text-sm font-medium text-foreground truncate max-w-xs">{file?.name}</span>
                  <span className="text-xs text-muted-foreground">{(file?.size / 1024 / 1024)?.toFixed(2)} MB</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <Icon name="Upload" size={28} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Click to select PDF</span>
                  <span className="text-xs text-muted-foreground">PDF only, max 50MB</span>
                </div>
              )}
            </label>
          </div>

          <div className="bg-accent/5 border border-accent/20 rounded-lg p-3 text-xs text-muted-foreground">
            <Icon name="Info" size={14} className="inline mr-1 text-accent" />
            If a drawing with the same Drawing Number already exists, the previous revision will automatically become <strong>Superseded</strong>.
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={uploading}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={uploading} iconName={uploading ? 'Loader2' : 'Upload'}>
              {uploading ? 'Uploading...' : 'Upload Drawing'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Drawing Row ─────────────────────────────────────────────────────────────
const DrawingRow = ({ drawing, onOpen, isSuperseded }) => {
  const uploadedByName = drawing?.uploaded_by_profile?.full_name || drawing?.uploaded_by_profile?.email || 'Unknown';
  const uploadDate = drawing?.upload_date
    ? new Date(drawing?.upload_date)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <div className={`flex items-center gap-4 p-4 rounded-lg border transition-smooth ${
      isSuperseded
        ? 'bg-muted/30 border-border opacity-75' :'bg-card border-border hover:border-accent/40 hover:shadow-sm'
    }`}>
      <div className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${
        isSuperseded ? 'bg-muted' : 'bg-accent/10'
      }`}>
        <Icon name="FileText" size={20} color={isSuperseded ? 'var(--color-muted-foreground)' : 'var(--color-accent)'} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-foreground text-sm truncate">{drawing?.drawing_name}</span>
          {!isSuperseded && (
            <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full flex-shrink-0">
              Current
            </span>
          )}
          {isSuperseded && (
            <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs font-medium rounded-full flex-shrink-0">
              Superseded
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {drawing?.drawing_number && (
            <span className="text-xs text-muted-foreground">
              <span className="font-medium">No.</span> {drawing?.drawing_number}
            </span>
          )}
          {drawing?.revision && (
            <span className="text-xs text-muted-foreground">
              <span className="font-medium">Rev.</span> {drawing?.revision}
            </span>
          )}
          <span className="text-xs text-muted-foreground">{uploadDate}</span>
          <span className="text-xs text-muted-foreground">by {uploadedByName}</span>
        </div>
      </div>

      <Button
        size="sm"
        variant={isSuperseded ? 'outline' : 'default'}
        iconName="ExternalLink"
        onClick={() => onOpen(drawing)}
        className="flex-shrink-0"
      >
        Open
      </Button>
    </div>
  );
};

// ─── Main DrawingsTab ─────────────────────────────────────────────────────────
const DrawingsTab = ({ siteId, siteData, drawings: initialDrawings, onRefresh, isReadOnly = false }) => {
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [openingDrawing, setOpeningDrawing] = useState(null);
  const [error, setError] = useState(null);
  const [allDrawings, setAllDrawings] = useState(null); // null = use initialDrawings (current only)
  const [loadingAll, setLoadingAll] = useState(false);
  const [showSuperseded, setShowSuperseded] = useState(false);

  const canUpload = !isReadOnly && (userProfile?.role === 'admin' || userProfile?.role === 'site_manager');

  // Use initialDrawings (current only) unless we've loaded all
  const drawingsToUse = allDrawings ?? initialDrawings ?? [];
  const currentDrawings = drawingsToUse?.filter(d => d?.drawing_status === 'current' || d?.is_latest === true);
  const supersededDrawings = drawingsToUse?.filter(d => d?.drawing_status === 'superseded' || (d?.drawing_status == null && d?.is_latest === false));

  const handleLoadSuperseded = async () => {
    if (allDrawings !== null) {
      setShowSuperseded(v => !v);
      return;
    }
    try {
      setLoadingAll(true);
      const result = await drawingService?.getAllDrawingsBySite(siteId);
      if (result?.data) {
        setAllDrawings(result?.data);
        setShowSuperseded(true);
      }
    } catch (err) {
      setError('Failed to load superseded drawings.');
    } finally {
      setLoadingAll(false);
    }
  };

  const handleOpenDrawing = async (drawing) => {
    try {
      setOpeningDrawing(drawing?.id);
      setError(null);
      const urlResult = await drawingService?.getDrawingFileUrl(drawing?.file_path);
      if (urlResult?.error || !urlResult?.url) {
        setError('Could not open drawing. The file may no longer be available.');
        return;
      }
      window?.open(urlResult?.url, '_blank');
    } catch (err) {
      setError('Failed to open drawing.');
    } finally {
      setOpeningDrawing(null);
    }
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    setAllDrawings(null); // reset so next load fetches fresh
    onRefresh?.();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Drawings</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {currentDrawings?.length} current drawing{currentDrawings?.length !== 1 ? 's' : ''}
            {supersededDrawings?.length > 0 && ` · ${supersededDrawings?.length} superseded`}
          </p>
        </div>
        {canUpload && (
          <Button
            size="sm"
            iconName="Upload"
            onClick={() => setShowUploadModal(true)}
          >
            Upload Drawing
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <Icon name="AlertCircle" size={16} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <Icon name="X" size={14} />
          </button>
        </div>
      )}

      {/* Current Drawings Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-success flex-shrink-0" />
          <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">
            Current Drawings
          </h4>
          <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded-full">
            {currentDrawings?.length}
          </span>
        </div>

        {currentDrawings?.length > 0 ? (
          <div className="space-y-2">
            {currentDrawings?.map(drawing => (
              <DrawingRow
                key={drawing?.id}
                drawing={drawing}
                onOpen={handleOpenDrawing}
                isSuperseded={false}
              />
            ))}
          </div>
        ) : (
          <div className="bg-muted/30 rounded-lg border border-dashed border-border p-10 text-center">
            <Icon name="FileText" size={40} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">No drawings uploaded yet</p>
            <p className="text-xs text-muted-foreground mb-4">
              Upload the latest drawings for this site so workers can access them on their phones.
            </p>
            {canUpload && (
              <Button size="sm" iconName="Upload" onClick={() => setShowUploadModal(true)}>
                Upload First Drawing
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Superseded Drawings Section */}
      <div>
        <button
          onClick={handleLoadSuperseded}
          disabled={loadingAll}
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-smooth mb-3 group"
        >
          <div className="w-2 h-2 rounded-full bg-muted-foreground flex-shrink-0" />
          <span className="uppercase tracking-wide text-xs">Superseded Drawings</span>
          {allDrawings !== null && (
            <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs font-medium rounded-full">
              {supersededDrawings?.length}
            </span>
          )}
          {loadingAll ? (
            <Icon name="Loader2" size={14} className="animate-spin" />
          ) : (
            <Icon name={showSuperseded ? 'ChevronUp' : 'ChevronDown'} size={14} className="group-hover:text-foreground" />
          )}
        </button>

        {showSuperseded && allDrawings !== null && (
          supersededDrawings?.length > 0 ? (
            <div className="space-y-2">
              {supersededDrawings?.map(drawing => (
                <DrawingRow
                  key={drawing?.id}
                  drawing={drawing}
                  onOpen={handleOpenDrawing}
                  isSuperseded={true}
                />
              ))}
            </div>
          ) : (
            <div className="bg-muted/20 rounded-lg border border-dashed border-border p-6 text-center">
              <p className="text-sm text-muted-foreground">No superseded drawings for this site.</p>
            </div>
          )
        )}

        {!showSuperseded && allDrawings === null && (
          <p className="text-xs text-muted-foreground pl-4">
            Click to view previous revisions that have been superseded.
          </p>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadDrawingModal
          siteId={siteId}
          siteData={siteData}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
};

export default DrawingsTab;